import {useEffect, useRef, useState} from 'react'
import type {KeyboardEvent as ReactKeyboardEvent, ReactElement} from 'react'
import {createPortal} from 'react-dom'
import {Icon, LoadingSpinner} from 'pepsico-ds'
import type {DocsArticleVideo} from '@/services/cms/docsCmsService'
import MinimalVideoControls from './MinimalVideoControls'
import {useHlsVideo} from './useHlsVideo'

const SEEK_STEP_SECONDS = 5
const DEFAULT_ASPECT_RATIO = 16 / 9
const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

interface PageDocsVideoModalProps {
  video: DocsArticleVideo
  onClose: () => void
}

const getBufferedEnd = (element: HTMLVideoElement): number => {
  const {buffered, currentTime} = element
  for (let index = 0; index < buffered.length; index++) {
    if (currentTime >= buffered.start(index) && currentTime <= buffered.end(index)) return buffered.end(index)
  }
  return 0
}

/** Dark modal player for the article video: click to play/pause, Space to toggle, arrow keys to seek, Esc to close. */
const PageDocsVideoModal = ({video, onClose}: PageDocsVideoModalProps): ReactElement => {
  const dialogRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isBuffering, setIsBuffering] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(video.durationSeconds ?? 0)
  const [bufferedEnd, setBufferedEnd] = useState(0)
  const {hasError, retry} = useHlsVideo(videoRef, {streamingUrl: video.streamingUrl, mp4Url: video.mp4Url})
  // The docs slider closes on any mousedown outside its own DOM, which the player portal would otherwise trigger.
  const [portalContainer] = useState(() => document.createElement('div'))

  useEffect(() => {
    const stopMouseDown = (event: MouseEvent): void => event.stopPropagation()
    portalContainer.addEventListener('mousedown', stopMouseDown)
    document.body.appendChild(portalContainer)
    return () => {
      portalContainer.removeEventListener('mousedown', stopMouseDown)
      portalContainer.remove()
    }
  }, [portalContainer])

  const label = video.title ?? 'Article video'
  const aspectRatio =
    video.width !== null && video.height !== null && video.height > 0
      ? video.width / video.height
      : DEFAULT_ASPECT_RATIO

  // Escape is handled in the capture phase so it closes only the player and not the docs slider behind it.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [onClose])

  // Move focus into the dialog and give it back to the thumbnail that opened it.
  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
    closeButtonRef.current?.focus()
    return () => previouslyFocused?.focus()
  }, [])

  const togglePlay = (): void => {
    const element = videoRef.current
    if (!element) return
    if (element.paused) {
      element.play().catch(() => setIsPlaying(false))
    } else {
      element.pause()
    }
  }

  const seekTo = (seconds: number): void => {
    const element = videoRef.current
    if (!element) return
    const limit = Number.isFinite(element.duration) ? element.duration : duration
    element.currentTime = Math.min(Math.max(seconds, 0), limit)
    setCurrentTime(element.currentTime)
  }

  const handleDialogKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement
    const isSlider = target instanceof HTMLInputElement

    if (event.key === 'Tab') {
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
      return
    }

    if (event.key === ' ' && target.tagName !== 'BUTTON') {
      event.preventDefault()
      togglePlay()
    } else if (!isSlider && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault()
      seekTo(
        (videoRef.current?.currentTime ?? 0) + (event.key === 'ArrowRight' ? SEEK_STEP_SECONDS : -SEEK_STEP_SECONDS),
      )
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/80 p-4"
      data-testid="page-docs-video-backdrop"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-testid="page-docs-video-modal"
        onClick={event => event.stopPropagation()}
        onKeyDown={handleDialogKeyDown}
        className="relative w-full overflow-hidden rounded-lg bg-black shadow-2xl"
        style={{maxWidth: `min(1100px, calc((100vh - 9rem) * ${aspectRatio}))`}}
      >
        <button
          ref={closeButtonRef}
          type="button"
          aria-label="Close video"
          data-testid="page-docs-video-close"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-none bg-black/60 text-white transition-colors hover:bg-black/80 focus-visible:outline-2 focus-visible:outline-white"
        >
          <Icon icon="close" size="medium" style={{color: 'currentColor'}} />
        </button>

        <div className="relative w-full bg-black" style={{aspectRatio}}>
          <video
            ref={videoRef}
            data-testid="page-docs-video-element"
            className="h-full w-full cursor-pointer bg-black"
            poster={video.thumbnailUrl ?? undefined}
            aria-label={label}
            autoPlay
            playsInline
            preload="metadata"
            onClick={togglePlay}
            onPlay={(): void => setIsPlaying(true)}
            onPause={(): void => setIsPlaying(false)}
            onEnded={(): void => setIsPlaying(false)}
            onWaiting={(): void => setIsBuffering(true)}
            onCanPlay={(): void => setIsBuffering(false)}
            onPlaying={(): void => setIsBuffering(false)}
            onLoadedMetadata={(event): void => {
              if (Number.isFinite(event.currentTarget.duration)) setDuration(event.currentTarget.duration)
            }}
            onDurationChange={(event): void => {
              if (Number.isFinite(event.currentTarget.duration)) setDuration(event.currentTarget.duration)
            }}
            onTimeUpdate={(event): void => {
              setCurrentTime(event.currentTarget.currentTime)
              setBufferedEnd(getBufferedEnd(event.currentTarget))
            }}
            onProgress={(event): void => setBufferedEnd(getBufferedEnd(event.currentTarget))}
          />

          {isBuffering && !hasError && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <LoadingSpinner size={{width: '40px', height: '40px'}} />
            </div>
          )}

          {hasError && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/85 text-center text-white"
              data-testid="page-docs-video-error"
            >
              <p className="m-0 text-sm">This video is unavailable right now.</p>
              <button
                type="button"
                data-testid="page-docs-video-retry"
                onClick={retry}
                className="cursor-pointer rounded-md border border-white/60 bg-transparent px-4 py-1.5 text-sm text-white hover:bg-white/15"
              >
                Try again
              </button>
            </div>
          )}
        </div>

        {!hasError && (
          <MinimalVideoControls
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            bufferedEnd={bufferedEnd}
            onTogglePlay={togglePlay}
            onSeek={seekTo}
          />
        )}
      </div>
    </div>,
    portalContainer,
  )
}

export default PageDocsVideoModal
